import { Group, Title, Text, Badge, Button, Stack } from '@mantine/core';
import type { ReactNode } from 'react';
import type { Icon } from '@tabler/icons-react';

interface SecondaryAction {
  /** Etiqueta del botón secundario (ej. "Emitir desde envío"). */
  label: string;
  /** Acción al hacer click. */
  onClick: () => void;
  /** Icono del botón. De @tabler/icons-react. */
  icon?: Icon;
  /** Variante del botón. Default: 'default' (gris, junto al primary). */
  variant?: 'default' | 'subtle' | 'light';
}

interface Props {
  title: string;
  subtitle?: string;
  /** Icono principal del encabezado. De @tabler/icons-react. */
  icon: Icon;
  /** Etiqueta del boton principal (ej. "Nuevo vehiculo"). */
  newLabel: string;
  /** Total de elementos (se pinta como contador discreto). */
  count?: number;
  /** Color del Badge del contador (ej. 'deca' o 'gray'). */
  countColor?: string;
  /** Accion del boton principal. */
  onNew: () => void;
  /** Slot para filtros extra a la derecha del titulo. */
  filters?: ReactNode;
  /**
   * Botón secundario opcional a la izquierda del principal.
   * Útil cuando una página tiene dos caminos de creación (ej. en DeCA:
   * "Crear DeCA" primary + "Emitir desde envío" secondary).
   */
  secondaryAction?: SecondaryAction;
}

/**
 * Encabezado compartido para todas las paginas de catalogos (vehiculos,
 * conductores, cargadores, transportistas, origenes/destinos, usuarios).
 *
 * El objetivo es que todas las paginas tengan el mismo aire:
 *  - Titulo + icono a la izquierda.
 *  - Badge con el contador (para saber cuantos hay sin abrir la lista).
 *  - Boton principal a la derecha.
 *  - Subtítulo opcional debajo.
 *  - Filtros opcionales a la izquierda (entre el titulo y el boton).
 *  - Botón secundario opcional (a la izquierda del principal), para
 *    páginas con varios flujos de alta (DeCA).
 */
export function CatalogPageHeader({
  title,
  subtitle,
  icon: Icon,
  newLabel,
  count,
  countColor = 'deca',
  onNew,
  filters,
  secondaryAction,
}: Props) {
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <Group gap="sm" align="center" wrap="nowrap" miw={0} style={{ minWidth: 0 }}>
          <Icon size={26} stroke={1.6} color="var(--mantine-color-deca-7)" />
          <Title order={2} fw={600} style={{ minWidth: 0 }}>
            {title}
          </Title>
          {typeof count === 'number' && (
            <Badge size="lg" variant="light" color={countColor} radius="sm">
              {count}
            </Badge>
          )}
        </Group>
        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          {filters}
          {secondaryAction && (
            <Button
              onClick={secondaryAction.onClick}
              size="sm"
              variant={secondaryAction.variant ?? 'default'}
              leftSection={secondaryAction.icon ? <secondaryAction.icon size={16} /> : undefined}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button onClick={onNew} size="sm" leftSection={<PlusIcon />}>
            {newLabel}
          </Button>
        </Group>
      </Group>
      {subtitle && (
        <Text c="dimmed" size="sm">
          {subtitle}
        </Text>
      )}
    </Stack>
  );
}

// Icono Plus minimo para no importar en cada cabecera.
function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
